import { ledger, type PaperTrade } from './ledger';
import { ASSET_CLASSES, type AssetClass } from '@ordo-mentis/ledger-store';

// Ports approved-cockpit.js readLedger/forecastStats over the typed ledger.
// Account split: Caesar = auto-paper-logged (notes start "auto paper"); Operator = manual.
const isAuto = (t: PaperTrade) => typeof t.notes === 'string' && /^auto paper/i.test(t.notes);
const isDone = (t: PaperTrade) =>
  !!(t.status === 'closed' || t.exitPrice != null || (t as Record<string, unknown>).exitDate || (t as Record<string, unknown>).outcome);
const isHit = (t: PaperTrade) => t.win === true || (typeof t.pl === 'number' && t.pl > 0);
const dateOf = (t: PaperTrade) =>
  Date.parse(String((t as Record<string, unknown>).exitDate || (t as Record<string, unknown>).date || '')) || 0;

export const tradeSym = (t: PaperTrade) =>
  String(t.symbol || (t as Record<string, unknown>).ticker || (t as Record<string, unknown>).pair || (t as Record<string, unknown>).coin || '-');
export const tradeWhat = (t: PaperTrade) =>
  String(t.type || (t as Record<string, unknown>).kind || (t as Record<string, unknown>).direction || (t as Record<string, unknown>).dir || '-');
export const tradeAcct = (t: PaperTrade) => (isAuto(t) ? 'Caesar' : 'Operator');
export const tradeIsHit = isHit;

export interface OutcomeRow {
  symbol: string;
  what: string;
  acct: string;
  hit: boolean;
}

export function resolvedOutcomeRows(limit = 8): OutcomeRow[] {
  const rows = ledger.allTrades().filter(isDone);
  rows.sort((a, b) => dateOf(b) - dateOf(a));
  return rows.slice(0, limit).map((t) => ({ symbol: tradeSym(t), what: tradeWhat(t), acct: tradeAcct(t), hit: isHit(t) }));
}

export interface PortfolioRow {
  asset: AssetClass;
  total: number;
  open: number;
  resolved: number;
  hits: number;
  miss: number;
  hitRate: number | null;
}

// Paper Portfolio: the per-asset-class aggregation the legacy /dashboard#portfolio
// nav link pointed at but never actually rendered. One row per asset class over the
// typed ledger, plus a 'total' rollup.
export function portfolioByAsset(): { rows: PortfolioRow[]; total: PortfolioRow } {
  const rows = ASSET_CLASSES.map((asset) => {
    const trades = ledger.getTrades(asset);
    const resolved = trades.filter(isDone);
    const hits = resolved.filter(isHit).length;
    return {
      asset,
      total: trades.length,
      open: trades.length - resolved.length,
      resolved: resolved.length,
      hits,
      miss: resolved.length - hits,
      hitRate: resolved.length ? hits / resolved.length : null,
    };
  });
  const sum = (k: keyof PortfolioRow) => rows.reduce((acc, r) => acc + (r[k] as number), 0);
  const tResolved = sum('resolved');
  const total: PortfolioRow = {
    asset: 'options',
    total: sum('total'),
    open: sum('open'),
    resolved: tResolved,
    hits: sum('hits'),
    miss: sum('miss'),
    hitRate: tResolved ? sum('hits') / tResolved : null,
  };
  return { rows, total };
}

export interface LedgerSummary {
  hits: number;
  miss: number;
  open: number;
  fcOpen: number;
  resolved: number;
  total: number;
  valid: number;
  series: number[];
}

export function ledgerSummary(): LedgerSummary {
  const all = ledger.allTrades();
  const resolved = all.filter(isDone);
  const hits = resolved.filter(isHit).length;
  const valid = all.filter((t) => t.id != null).length;
  const fc = ledger.getForecasts();
  const fcOpen = fc.filter((r) => r && !(r as Record<string, unknown>).closed).length;
  const series = [0];
  resolved
    .slice()
    .sort((a, b) => dateOf(a) - dateOf(b))
    .forEach((t) => series.push(series[series.length - 1] + (isHit(t) ? 1 : -1)));
  return {
    hits,
    miss: resolved.length - hits,
    open: all.length - resolved.length,
    fcOpen,
    resolved: resolved.length,
    total: all.length,
    valid,
    series,
  };
}
